import React, { useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";

import { useDomainCheck } from "../../lib/hooks/useDomainCheck";
import DefaultLogin from "./DefaultLogin";
import CenterParcsLogin from "./CenterParcsLogin";
import { useAuth } from "../../context/Auth";

type FieldValues = {
  email: string;
  password: string;
};

const Login = React.memo(() => {
  const { t } = useTranslation(["login", "common"]);

  const { replace } = useHistory();

  const { login } = useAuth();

  const { isCenterParcsDomain } = useDomainCheck();

  const schema = useMemo(() => {
    return yup.object().shape({
      email: yup
        .string()
        .required(t("login:yup.email.required"))
        .email(t("login:yup.email.pattern")),
      password: yup.string().required(t("login:yup.password.required")),
    });
  }, [t]);

  const methods = useForm<FieldValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      email:
        process.env.NODE_ENV === "development"
          ? "webmaster@futureforward.rs"
          : "",
      password: process.env.NODE_ENV === "development" ? "admin" : "",
    },
  });

  const onSubmit = useCallback(
    (data: FieldValues) => {
      const { password } = data;

      const email = data.email.trim().toLowerCase();

      return login(email, password)
        .then((res) => {
          const {
            data: {
              login: { requires2fa, token },
            },
          } = res;

          if (!requires2fa) return;

          replace("/verify-2fa", { token });
        })
        .catch((error) => {
          toast.error<string>(
            error?.networkError?.result?.errors?.[0]?.message ?? error?.message,
          );
        });
    },
    [login, replace],
  );

  const loginProps = {
    onSubmit: methods.handleSubmit(onSubmit),
    t,
  };

  if (isCenterParcsDomain) {
    return (
      <FormProvider {...methods}>
        <CenterParcsLogin {...loginProps} />
      </FormProvider>
    );
  }

  return (
    <FormProvider {...methods}>
      <DefaultLogin {...loginProps} />
    </FormProvider>
  );
});

export default Login;
